// src/api/user.ts
// Thin API layer that *does not* force Content-Type headers.
// Axios (or your client) will set the correct multipart boundary for FormData automatically.

import { commonsApiClient } from "./client";
import { UserProfileResponse, UsageEventResponse } from "../types/user";

/**
 * Post FormData and return the *body*.
 * Unwraps axios-style `{ data }` if present; otherwise returns the value directly.
 */
async function postForm<T = any>(url: string, fd: FormData): Promise<T> {
  const config = {
    headers: {
      "Content-Type": undefined,
    },
  };

  const resp = await (commonsApiClient as any).post(url, fd, config);

  if (resp && typeof resp === "object" && "data" in resp) {
    return (resp as { data: T }).data;
  }
  return resp as T;
}

export interface UserDetailsWithMore {
  userData: any;
  allDisciplines: { value: string; label: string }[];
  allIndustries: { value: string; label: string }[];
}

export const userApi = {
  /**
   * Fetches a user profile by ID
   * @param userId - The ID of the user to fetch
   * @returns Promise resolving to user profile data with recipes
   */
  async getUserProfile(userId: string): Promise<UserProfileResponse> {
    const user = await commonsApiClient.get<UserProfileResponse>(
      `/api/user-directory/${userId}`
    );
    return user;
  },
  /**
   * Update profile details (optionally with an `image` File).
   * Keys: name, last_name, email, location, city, institute, description, image?
   */
  updateDetails: (fd: FormData) => postForm("/api/user/update/details", fd),

  /**
   * Update social links. Keys (only include if present): twitter, linkedIn, instagram.
   */
  updateSocial: (fd: FormData) => postForm("/api/user/update/social", fd),

  /**
   * Update interests. Keys: disciplines, industries as CSV/JSON strings; keywords as plain string.
   */
  updateInterests: (fd: FormData) => postForm("/api/user/update/interests", fd),

  /**
   * Update password. Keys: password, password_confirmation.
   */
  updatePassword: (fd: FormData) => postForm("/api/user/update/password", fd),

  /**
   * Tracks a usage event for the current user and web route
   * Supports both authenticated and anonymous users
   * @param webRoute - The web route being accessed
   * @param userId - The user ID (optional, null for anonymous users)
   * @returns Promise resolving to usage event response
   */
  async trackUsageEvent(
    webRoute: string,
    userId?: string | null
  ): Promise<UsageEventResponse> {
    const userIdParam = userId ? `&userId=${encodeURIComponent(userId)}` : "";
    return commonsApiClient.post<UsageEventResponse>(
      `/api/usage-event?webRoute=${encodeURIComponent(webRoute)}${userIdParam}`
    );
  },

  /**
   * Fetches user details along with additional data like disciplines and industries
   * @returns Promise resolving to user details with additional data
   */
  async getUserDetailsWithMore(): Promise<UserDetailsWithMore> {
    return commonsApiClient.get<UserDetailsWithMore>("/api/user/details");
  },
};
